import { createSlice, createAction, isAnyOf } from "@reduxjs/toolkit";
import {
  RouteViewModel,
  routeViewModelT,
  routeViewModelsT,
} from "dora-contracts";
import axios, { getData } from "../../../axios";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as tPromise from "io-ts-promise";
import keyBy from "lodash/keyBy";
import { moveCargo } from "../../dispatchActions";
import {
  addDriver,
  addRoute,
  archiveRoute,
  getAllArchivedRoutes,
  removeDriver,
  removeRouteTrailer,
  removeRouteTruck,
  updateRouteTrailer,
  updateRouteTruck,
} from "../../routes";
import { AppThunkAction } from "../../../redux-store";
import * as authSelectors from "../../auth/selectors";
import { cargoViewT, CargoView } from "../cargo-views/types";
import * as t from "io-ts";
import { debouncedLoadCalendarViewData } from "../calendar-view";
import { loadCargoViewsForRoutes } from "../cargo-views";
import { selectRoutesSearchDates } from "../../app/dispatching/selectors";
import { isEqual } from "lodash";
import uniq from "lodash/uniq";

const prefix = "data/route-views";

type RouteVehicleField = "truckId" | "unpoweredTrailerId";

// A truck/trailer change the user made that the server hasn't confirmed yet,
// keyed by the thunk's requestId. The new value is shown straight away; the
// route's `route-view.updated` socket message only arrives some time after
// the request returns, and until then the field would otherwise snap back to
// the old vehicle.
type PendingVehicleChange = {
  routeId: string;
  field: RouteVehicleField;
  value: string | null;
  previous: string | null;
};

// Same idea for adding/removing a driver on a route.
type PendingDriverChange = {
  routeId: string;
  driverId: string;
  assigned: boolean;
};

type State = {
  entities: Record<string, RouteViewModel | undefined>;
  routeIds: string[];
  pendingVehicleChanges: Record<string, PendingVehicleChange>;
  pendingDriverChanges: Record<string, PendingDriverChange>;
  // loading: boolean;
};

const initialState: State = {
  entities: {},
  routeIds: [],
  pendingVehicleChanges: {},
  pendingDriverChanges: {},
  //loading: false
};

const withDriverAssigned = (
  drivers: string[],
  driverId: string,
  assigned: boolean
) =>
  assigned
    ? drivers.includes(driverId)
      ? drivers
      : [...drivers, driverId]
    : drivers.filter((d) => d !== driverId);

// A view model that arrives while a change is pending was built before the
// server saw that change, so it would revert the field; keep the pending value.
const reapplyPendingChanges = (state: State) => {
  for (const change of Object.values(state.pendingDriverChanges)) {
    const entity = state.entities[change.routeId];
    if (entity && entity.drivers.includes(change.driverId) !== change.assigned) {
      // Replace rather than assign into it: see below.
      state.entities[change.routeId] = {
        ...entity,
        drivers: withDriverAssigned(
          entity.drivers,
          change.driverId,
          change.assigned
        ),
      };
    }
  }
  for (const change of Object.values(state.pendingVehicleChanges)) {
    const entity = state.entities[change.routeId];
    // The entity may be the incoming payload itself rather than an immer
    // draft, so replace it with a copy instead of assigning into it.
    if (entity && entity[change.field] !== change.value) {
      state.entities[change.routeId] = {
        ...entity,
        [change.field]: change.value,
      };
    }
  }
};

export const loadRouteViewModels = createErrorReportingAsyncThunk(
  `${prefix}/load-all`,
  async (_, { dispatch, getState }) => {
    const state = getState();
    const routeSearchDates = selectRoutesSearchDates(state);
    const input = routeSearchDates
      ? {
          startDate: routeSearchDates.fromDate,
          endDate: routeSearchDates.toDate,
        }
      : undefined;
    let url = "/api/routes/view-models";
    if (input) {
      url += `?startDate=${input.startDate}&endDate=${input.endDate}`;
    }
    const data = await axios.get(url).then(getData);
    const routeViewModels = await tPromise.decode(routeViewModelsT)(data);
    if (routeViewModels.length) {
      dispatch(loadCargoViewsForRoutes(routeViewModels.map((r) => r.id)));
    }
    // Driver activities for these routes are loaded by the listener in
    // ../driver-activities/listeners.ts, once features are known.
    return routeViewModels;
  }
);

// One action per batch of socket `route-view.updated` messages, not one per
// message: a burst of hundreds (nightly schedules, public API imports) applied
// one dispatch per frame pushes React 19 past its nested update limit
// (DORA-385). See SocketConnection.
export const routeViewModelsDecoded = createAction<{
  models: { route: RouteViewModel; cargos: CargoView[] }[];
  userTeams: string[];
}>(`${prefix}/models-updated`);

const pruneStaleRouteViewModels = createAction<{
  freshRouteIds: string[];
  startDate?: string;
  endDate?: string;
}>(`${prefix}/prune-stale`);

// loadRouteViewModels only ever adds routeIds, since routes from other
// previously-viewed date ranges are intentionally kept cached. On
// reconnect though, a route within the range we just fetched may have
// disappeared (archived, reassigned, deleted) while we were disconnected
// and missed the `route-view.updated` message that would have removed it.
// Reconcile just that overlap instead of touching the general cache.
export const resyncRouteViewModelsAfterReconnect =
  (): AppThunkAction => async (dispatch, getState) => {
    const routeSearchDates = selectRoutesSearchDates(getState());
    let freshRouteViewModels;
    try {
      freshRouteViewModels = await dispatch(loadRouteViewModels()).unwrap();
    } catch {
      // loadRouteViewModels has already reported it (or deliberately not, for
      // a 401 / network error) and shown the toast. Callers dispatch this from
      // the socket reconnect handler without awaiting it, so re-throwing would
      // only surface as an unhandled rejection (DISPATCHER-4). Without a fresh
      // list there's nothing to reconcile against, so skip the prune.
      return;
    }
    dispatch(
      pruneStaleRouteViewModels({
        freshRouteIds: freshRouteViewModels.map((r) => r.id),
        startDate: routeSearchDates?.fromDate,
        endDate: routeSearchDates?.toDate,
      })
    );
  };

const viewModelUpdatedT = t.strict({
  route: routeViewModelT,
  cargos: t.array(cargoViewT),
});

export const viewModelsUpdated =
  (inputs: unknown[]): AppThunkAction =>
  (dispatch, getState) => {
    const models: t.TypeOf<typeof viewModelUpdatedT>[] = [];
    let invalid = 0;
    for (const input of inputs) {
      const result = viewModelUpdatedT.decode(input);
      if (result._tag === "Right") {
        models.push(result.right);
      } else {
        invalid++;
      }
    }
    if (models.length > 0) {
      const userTeams = authSelectors.selectMyTeamsIds(getState());
      dispatch(routeViewModelsDecoded({ models, userTeams }));
      if (getState().data.calendarView.pageInView) {
        debouncedLoadCalendarViewData(dispatch);
      }
    }
    // Apply the valid ones first, so one bad message doesn't drop the batch.
    if (invalid > 0) {
      throw new Error("Invalid input");
    }
  };

export const viewModelUpdated = (input: unknown): AppThunkAction =>
  viewModelsUpdated([input]);

const startVehicleChange = (
  state: State,
  requestId: string,
  routeId: string,
  field: RouteVehicleField,
  value: string | null
) => {
  const entity = state.entities[routeId];
  if (!entity) {
    return;
  }
  state.pendingVehicleChanges[requestId] = {
    routeId,
    field,
    value,
    previous: entity[field],
  };
  entity[field] = value;
};

const startDriverChange = (
  state: State,
  requestId: string,
  arg: { routeId: string; driverId: string },
  assigned: boolean
) => {
  const entity = state.entities[arg.routeId];
  if (!entity || entity.drivers.includes(arg.driverId) === assigned) {
    return;
  }
  state.pendingDriverChanges[requestId] = {
    routeId: arg.routeId,
    driverId: arg.driverId,
    assigned,
  };
  entity.drivers = withDriverAssigned(entity.drivers, arg.driverId, assigned);
};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadRouteViewModels.fulfilled, (state, action) => {
        const isSubset = (
          stateEntities: Record<string, any>,
          payloadEntities: Record<string, any>
        ) => {
          return Object.keys(payloadEntities).every(
            (key) =>
              stateEntities[key] &&
              isEqual(stateEntities[key], payloadEntities[key])
          );
        };

        const payloadEntities = keyBy(action.payload, "id");
        if (!isSubset(state.entities, payloadEntities)) {
          state.entities = {
            ...state.entities,
            ...keyBy(action.payload, "id"),
          };
        }

        if (action.payload.some((y) => !state.routeIds.includes(y.id))) {
          state.routeIds = uniq([
            ...state.routeIds,
            ...action.payload.map((x) => x.id),
          ]);
        }
        reapplyPendingChanges(state);
      })
      .addCase(pruneStaleRouteViewModels, (state, action) => {
        const { freshRouteIds, startDate, endDate } = action.payload;
        const freshSet = new Set(freshRouteIds);
        state.routeIds = state.routeIds.filter((id) => {
          if (freshSet.has(id)) {
            return true;
          }
          const entity = state.entities[id];
          if (!entity) {
            return true;
          }
          // Mirrors the server's own date filter (route-views.ts findForUser):
          // if this route's dates fall inside the range we just queried, it
          // should have come back in the response. Since it didn't, it's no
          // longer visible to us (archived, reassigned, deleted) — drop it.
          // Anything outside the queried range is untouched cross-range cache.
          const withinQueriedRange =
            (entity.startDate === null ||
              !endDate ||
              entity.startDate <= endDate) &&
            (entity.endDate === null ||
              !startDate ||
              entity.endDate >= startDate);
          return !withinQueriedRange;
        });
      })
      .addCase(routeViewModelsDecoded, (state, action) => {
        const { models, userTeams } = action.payload;
        for (const { route } of models) {
          const routeId = route.id;
          state.entities[routeId] = route;
          const userCanSeeRoute =
            userTeams.includes(route.assignedTeamId) && !route.archived;
          if (userCanSeeRoute) {
            if (!state.routeIds.includes(routeId)) {
              state.routeIds.unshift(routeId);
            }
          } else {
            state.routeIds = state.routeIds.filter((x) => x !== routeId);
          }
        }
        reapplyPendingChanges(state);
      })
      .addCase(moveCargo.fulfilled, (state, action) => {
        for (const route of action.payload.updatedCargoOrders) {
          const entity = state.entities[route.routeId];
          if (entity) {
            entity.cargoOrder = route.cargoOrder;
          }
        }
      })
      .addCase(updateRouteTruck.pending, (state, action) => {
        const { id, truckId } = action.meta.arg;
        startVehicleChange(state, action.meta.requestId, id, "truckId", truckId);
      })
      .addCase(removeRouteTruck.pending, (state, action) => {
        const { id } = action.meta.arg;
        startVehicleChange(state, action.meta.requestId, id, "truckId", null);
      })
      .addCase(updateRouteTrailer.pending, (state, action) => {
        const { id, trailerId } = action.meta.arg;
        startVehicleChange(
          state,
          action.meta.requestId,
          id,
          "unpoweredTrailerId",
          trailerId
        );
      })
      .addCase(removeRouteTrailer.pending, (state, action) => {
        const { id } = action.meta.arg;
        startVehicleChange(
          state,
          action.meta.requestId,
          id,
          "unpoweredTrailerId",
          null
        );
      })
      .addCase(addDriver.pending, (state, action) => {
        startDriverChange(state, action.meta.requestId, action.meta.arg, true);
      })
      .addCase(removeDriver.pending, (state, action) => {
        startDriverChange(state, action.meta.requestId, action.meta.arg, false);
      })
      .addCase(archiveRoute.fulfilled, (state, action) => {
        // Don't wait for the route-view.updated socket message to drop the
        // route: it only reaches users in the route's assigned team, and is
        // lost entirely if the socket is disconnected.
        const routeId = action.meta.arg.id;
        const entity = state.entities[routeId];
        if (entity) {
          entity.archived = true;
        }
        state.routeIds = state.routeIds.filter((x) => x !== routeId);
      })
      .addCase(getAllArchivedRoutes.fulfilled, (state, action) => {
        const routeViews = keyBy(action.payload.routeViews, "id");
        state.entities = { ...state.entities, ...routeViews };
      })
      .addCase(addRoute.fulfilled, (state, action) => {
        const routeId = action.payload.id;
        if (!state.entities[routeId]) {
          state.entities[routeId] = {
            id: routeId,
            cargoOrder: [],
            truckId: null,
            unpoweredTrailerId: null,
            note: null,
            ref: null,
            intendedStartDate: action.meta.arg?.intendedStartDate || null,
            assignedTeamId: action.payload.assignedTeamId,
            lastStop: null,
            firstStop: null,
            startDate: null,
            endDate: null,
            name: null,
            drivers: [],
            driverLogins: {},
            stopStatuses: {},
            archived: false,
            waypointNote: null,
            operationInProgress: false,
          };
        }
        if (!state.routeIds.includes(routeId)) {
          state.routeIds.unshift(routeId);
        }
      })
      .addMatcher(
        isAnyOf(
          updateRouteTruck.fulfilled,
          removeRouteTruck.fulfilled,
          updateRouteTrailer.fulfilled,
          removeRouteTrailer.fulfilled
        ),
        (state, action) => {
          delete state.pendingVehicleChanges[action.meta.requestId];
        }
      )
      .addMatcher(
        isAnyOf(addDriver.fulfilled, removeDriver.fulfilled),
        (state, action) => {
          delete state.pendingDriverChanges[action.meta.requestId];
        }
      )
      .addMatcher(
        isAnyOf(addDriver.rejected, removeDriver.rejected),
        (state, action) => {
          const change = state.pendingDriverChanges[action.meta.requestId];
          if (!change) {
            return;
          }
          delete state.pendingDriverChanges[action.meta.requestId];
          const entity = state.entities[change.routeId];
          // Only roll back if nothing newer has changed it since.
          if (
            entity &&
            entity.drivers.includes(change.driverId) === change.assigned
          ) {
            entity.drivers = withDriverAssigned(
              entity.drivers,
              change.driverId,
              !change.assigned
            );
          }
        }
      )
      .addMatcher(
        isAnyOf(
          updateRouteTruck.rejected,
          removeRouteTruck.rejected,
          updateRouteTrailer.rejected,
          removeRouteTrailer.rejected
        ),
        (state, action) => {
          const change = state.pendingVehicleChanges[action.meta.requestId];
          if (!change) {
            return;
          }
          delete state.pendingVehicleChanges[action.meta.requestId];
          const entity = state.entities[change.routeId];
          // Only roll back if nothing newer has replaced the optimistic value.
          if (entity && entity[change.field] === change.value) {
            entity[change.field] = change.previous;
          }
        }
      );
  },
});

export default slice.reducer;
